import React, { KeyboardEventHandler } from 'react';
import { isVisible } from 'element-is-visible';

export enum ArrowDirection {
	LeftRight,
	UpDown,
}

export type FocusManagerProps = {
	whitelistSelector?: string;
	direction?: ArrowDirection;
	includeHidden?: boolean;
};

interface FocusManagerOptions {}

interface FocusManager {
	/** Moves focus to the next focusable or tabbable element in the focus scope. */
	focusNext(opts?: FocusManagerOptions): HTMLElement | null;
	/** Moves focus to the previous focusable or tabbable element in the focus scope. */
	focusPrevious(opts?: FocusManagerOptions): HTMLElement | null;
	focusFirst(opts?: FocusManagerOptions): HTMLElement | null;
	focusLast(opts?: FocusManagerOptions): HTMLElement | null;
}

// @ts-ignore
const FocusContext = React.createContext<FocusManager>(null);

interface FocusScopeProps {
	/** The contents of the focus scope. */
	children: React.ReactNode;
	whitelistSelector?: string;
	includeHidden?: boolean;
}
function FocusScope(props: FocusScopeProps) {
	let { children } = props;
	let startRef = React.useRef<HTMLSpanElement>(null);
	let endRef = React.useRef<HTMLSpanElement>(null);
	let scopeRef = React.useRef<HTMLElement[]>([]);

	React.useLayoutEffect(() => {
		if (!startRef.current) {
			return;
		}

		// Find all rendered nodes between the sentinels and add them to the scope.
		let node = startRef.current.nextSibling;
		let nodes: HTMLSpanElement[] = [];
		while (node && node !== endRef.current) {
			nodes.push(node as HTMLSpanElement);
			node = node.nextSibling;
		}

		// @ts-ignore
		scopeRef.current = nodes;
	}, [children]);

	let focusManager = createFocusManagerForScope(scopeRef, props);

	return (
		<FocusContext.Provider value={focusManager}>
			<span hidden ref={startRef} />
			{children}
			<span hidden ref={endRef} />
		</FocusContext.Provider>
	);
}

function createFocusManagerForScope(scopeRef: React.RefObject<HTMLElement[]>, props: FocusManagerProps): FocusManager {
	return {
		focusNext(opts: FocusManagerOptions = {}) {
			let scope = scopeRef.current;
			let node = document.activeElement;

			if (!scope || scope.length === 0 || !node) {
				return null;
			}

			for (const curScope of scope) {
				let walker = getFocusableTreeWalker(curScope, props, node);
				walker.currentNode = node;
				let nextNode = walker.nextNode() as HTMLElement;
				if (nextNode) {
					nextNode.focus();
					return nextNode;
				}
			}
			return null;
		},
		focusPrevious(opts: FocusManagerOptions = {}) {
			let scope = scopeRef.current;
			let node = document.activeElement;
			if (!scope || scope.length === 0 || !node) {
				return null;
			}

			let walker = getFocusableTreeWalker(scope[0], props, node);
			walker.currentNode = node;
			let previousNode = walker.previousNode() as HTMLElement;
			if (previousNode) {
				previousNode.focus();
			}
			return previousNode;
		},
		focusFirst(opts: FocusManagerOptions = {}) {
			let scope = scopeRef.current;
			let node = document.activeElement;
			if (!scope || scope.length === 0 || !node) {
				return null;
			}

			let walker = getFocusableTreeWalker(scope[0], props, node);
			walker.currentNode = node;
			let previousNode = walker.previousNode() as HTMLElement;
			if (!previousNode) {
				return null;
			}
			let lastPreviousNode = previousNode;
			while (previousNode) {
				lastPreviousNode = previousNode;
				previousNode = walker.previousNode() as HTMLElement;
			}
			if (lastPreviousNode) {
				lastPreviousNode.focus();
				return lastPreviousNode;
			}

			return null;
		},
		focusLast(opts: FocusManagerOptions = {}) {
			let scope = scopeRef.current;
			let node = document.activeElement;
			if (!scope || scope.length === 0 || !node) {
				return null;
			}

			let walker = getFocusableTreeWalker(scope[scope.length - 1], props, node);
			walker.currentNode = node;
			let nextNode = walker.nextNode() as HTMLElement;
			if (!nextNode) {
				return null;
			}
			let lastNextNode = nextNode;
			while (nextNode) {
				lastNextNode = nextNode;
				nextNode = walker.nextNode() as HTMLElement;
			}
			if (lastNextNode) {
				lastNextNode.focus();
			}
			return lastNextNode;
		},
	};
}
const focusableElements = [
	'input:not([disabled]):not([type=hidden])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'button:not([disabled])',
	'a[href]',
	'area[href]',
	'summary',
	'iframe',
	'object',
	'embed',
	'audio[controls]',
	'video[controls]',
	'[contenteditable]',
];
const FOCUSABLE_ELEMENT_SELECTOR =
	focusableElements.join(':not([hidden]),') + ',[tabindex]:not([disabled]):not([hidden])';

focusableElements.push('[tabindex]:not([tabindex="-1"]):not([disabled])');

export function getFocusableTreeWalker(root: HTMLElement, props: FocusManagerProps, cur: Element) {
	let selector = FOCUSABLE_ELEMENT_SELECTOR;
	let walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
		acceptNode(node) {
			if (node === document.activeElement) {
				return NodeFilter.FILTER_SKIP;
			}
			if (props.whitelistSelector && !(node as HTMLElement).matches(props.whitelistSelector)) {
				return NodeFilter.FILTER_SKIP;
			}

			if (!(node as HTMLElement).matches(selector)) {
				return NodeFilter.FILTER_SKIP;
			}

			if (props.includeHidden) {
				return NodeFilter.FILTER_ACCEPT;
			}

			const isNodeVisible = isVisible(node as HTMLElement);
			if (!isNodeVisible) {
				return NodeFilter.FILTER_SKIP;
			}

			return NodeFilter.FILTER_ACCEPT;
		},
	});

	return walker;
}
/**
 * Returns a FocusManager interface for the parent FocusScope.
 * A FocusManager can be used to programmatically move focus within
 * a FocusScope, e.g. in response to user events like keyboard navigation.
 */
export function useFocusManager(): FocusManager {
	return React.useContext(FocusContext);
}

export const FocusManager: React.FC<FocusManagerProps> = ({
	children,
	whitelistSelector,
	direction = ArrowDirection.LeftRight,
	includeHidden,
}) => {
	if (!children) {
		if (process.env.NODE_ENV === 'development') {
			throw new Error(`FocusManager requires children`);
		} else {
			return null;
		}
	}

	return (
		<FocusScope whitelistSelector={whitelistSelector} includeHidden={includeHidden}>
			{React.Children.map(children, child => {
				const WrappedChildren: React.FC = () => {
					const focusManager = useFocusManager();
					const onKeyDown: KeyboardEventHandler = event => {
						switch (event.key) {
							case 'ArrowRight':
								if (direction === ArrowDirection.LeftRight) {
									event.preventDefault();
									focusManager.focusNext();
								}

								break;
							case 'ArrowDown':
								if (direction === ArrowDirection.UpDown) {
									event.preventDefault();
									focusManager.focusNext();
								}
								break;
							case 'ArrowLeft':
								if (direction === ArrowDirection.LeftRight) {
									event.preventDefault();
									focusManager.focusPrevious();
								}
								break;
							case 'ArrowUp':
								if (direction === ArrowDirection.UpDown) {
									event.preventDefault();
									focusManager.focusPrevious();
								}
								break;
							case 'Home':
								event.preventDefault();
								focusManager.focusFirst();

								break;
							case 'End':
								event.preventDefault();
								focusManager.focusLast();

								break;
						}
					};

					return React.cloneElement(
						// @ts-ignore
						child,
						{ onKeyDown },
					);
				};
				return <WrappedChildren />;
			})}
		</FocusScope>
	);
};
